import { useEffect } from 'react';
import useUserDataStore from './useUserDataStore';

/**
 * Custom hook to manage user data (security roles and hierarchy nodes)
 * Provides easy access to the data and loading states
 */
export const useUserData = () => {
  const {
    securityRoles,
    securityRolesLoading,
    securityRolesError,
    currentUserRole,
    hierarchyNodes,
    hierarchyNodesLoading,
    hierarchyNodesError,
    fetchSecurityRoles,
    fetchHierarchyNodes,
    setSecurityRoles,
    setCurrentUserRole,
    setHierarchyNodes,
    clearUserData,
    initializeFromLocalStorage,
    refreshCurrentUserRole,
  } = useUserDataStore();

  // Initialize data from localStorage on first mount
  useEffect(() => {
    initializeFromLocalStorage();
  }, [initializeFromLocalStorage]);

  // Computed properties
  const isLoading = securityRolesLoading || hierarchyNodesLoading;
  const hasErrors = securityRolesError !== null || hierarchyNodesError !== null;
  const hasData = securityRoles !== null || hierarchyNodes.length > 0;

  // Refresh all user data
  const refreshUserData = async () => {
    try {
      await Promise.all([
        fetchSecurityRoles(),
        fetchHierarchyNodes()
      ]);
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  return {
    // Data
    securityRoles,
    hierarchyNodes,
    currentUserRole,
    
    // Loading states
    securityRolesLoading,
    hierarchyNodesLoading,
    isLoading,
    
    // Error states
    securityRolesError,
    hierarchyNodesError,
    hasErrors,
    
    // Computed
    hasData,
    
    // Actions
    fetchSecurityRoles,
    fetchHierarchyNodes,
    refreshUserData,
    setSecurityRoles,
    setCurrentUserRole,
    setHierarchyNodes,
    clearUserData,
    refreshCurrentUserRole,
  };
};

export default useUserData;
